import { Credential, CredentialExchangeRecord, CredentialProofRequest } from '../types/credentials';
import { AllItems, RecordFilters } from '../types/interfaces';
import { IUserReservation } from '../types/reservation';
import { CredentialExchange, PresentationExchange } from '../types/traction';

import log from '../logger';
/**
 * Traction Service
 *
 * @class TractionService
 * @extends {BaseService}
 */
export default class TractionService {
  constructor(private token: string) {
    this.token = token;
  }

  public refreshToken(token: string): void {
    this.token = token;
  }

  public getToken(): string {
    return this.token;
  }
  /**
   * Get Credentials
   *
   * @return {*}  {Promise<Credential[]>}
   * @memberof TractionService
   */
  public async getCredentials(): Promise<Credential[]> {
    try {
      const response = await fetch('/api/credential', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tenant');
      }

      const data = await response.json();

      return data;
    } catch (error) {
      log.error('Error fetching items:', error);
      return [];
    }
  }

  /**
   * Get Credential Records
   *
   * @param {RecordFilters} [filters]
   * @return {*}  {Promise<CredentialExchange[]>}
   * @memberof TractionService
   */
  public async getCredentialRecords(filters?: RecordFilters): Promise<CredentialExchangeRecord[]> {
    const queryParams = new URLSearchParams();

    if (filters?.role) {
      queryParams.append('role', filters?.role);
    }
    if (filters?.state) {
      queryParams.append('state', filters?.state);
    }

    const queryString = queryParams.toString();
    try {
      const response = await fetch(
        `/api/credential/offers${queryString ? `?${queryString}` : ''}`,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch tenant');
      }

      const data = await response.json();

      return data;
    } catch (error) {
      log.error('Error fetching items:', error);
      return [];
    }
  }

  /**
   * Accept Credential Record
   *
   * @param {string} credentialExchangeId
   * @return {*}  {Promise<CredentialExchange>}
   * @memberof TractionService
   */
  public async acceptCredentialRecord(
    credentialExchangeId: string
  ): Promise<CredentialExchange | undefined> {
    try {
      const res = await fetch(`/api/credential/accept/${credentialExchangeId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch tenant');
      }

      const body: CredentialExchange = await res.json();

      return body;
    } catch (error) {
      log.error('Error accepting credential:', error);
    }
  }

  /**
   * Remove Credential
   *
   * @param {string} credentialExchangeId
   * @return {*}  {Promise<boolean>}
   * @memberof TractionService
   */
  public async removeCredentialRecord(credentialExchangeId: string): Promise<boolean> {
    try {
      const res = await fetch(`/api/credential/remove-offer/${credentialExchangeId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch tenant');
      }

      const body = await res.json();

      return body === true;
    } catch (error) {
      log.error('Error removing credential record: ', error);
      return false;
    }
  }

  // Proofs
  /**
   * Get Proof Requests
   *
   * @param {RecordFilters} [filters]
   * @return {*}  {Promise<CredentialProofRequest[]>}
   * @memberof TractionService
   */
  public async getProofRecords(filters?: RecordFilters): Promise<CredentialProofRequest[]> {
    const queryParams = new URLSearchParams();

    if (filters?.role) {
      queryParams.append('role', filters?.role);
    }
    if (filters?.state) {
      queryParams.append('state', filters?.state);
    }

    const queryString = queryParams.toString();
    try {
      const response = await fetch(`/api/proof${queryString ? `?${queryString}` : ''}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tenant');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      log.error('Error fetching items:', error);
      return [];
    }
  }

  public async receiveConnectionlessProofRequest(url: string): Promise<CredentialProofRequest[]> {
    try {
      const response = await fetch(`/api/proof/connectionless-request`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        },
        body: JSON.stringify({
          url: url
        })
      });

      const data: CredentialProofRequest[] = await response.json();
      return data;
    } catch (error) {
      log.error('Error receiving proof request: ', error);
      return [];
    }
  }

  /**
   * Send Proof Presentation
   *
   * @param {string} presentationExchangeId
   * @param {string} referentId
   * @param {*} requested_attributes
   * @param {*} requested_predicates
   * @return {*}  {(Promise<PresentationExchange | undefined>)}
   * @memberof TractionService
   */
  public async sendProofPresentation(
    presentationExchangeId: string,
    referentId: string,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    requested_attributes: any,
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    requested_predicates: any
  ): Promise<PresentationExchange | undefined> {
    try {
      const response = await fetch(`/api/proof/send-presentation`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        },
        body: JSON.stringify({
          presentationExchangeId: presentationExchangeId,
          referentId: referentId,
          requested_attributes: requested_attributes,
          requested_predicates: requested_predicates
        })
      });

      if (!response.ok) {
        throw new Error('Failed to send proof presentation');
      }

      const data: PresentationExchange = await response.json();
      return data;
    } catch (error) {
      log.error('Error verifying presentation:', error);
    }
  }

  /**
   * Reject Proof Request
   *
   * @param {string} presentationExchangeId
   * @param {string} reason
   * @return {*}  {Promise<boolean>}
   * @memberof TractionService
   */
  public async rejectProofRequest(
    presentationExchangeId: string,
    reason: string
  ): Promise<boolean> {
    try {
      const response = await fetch(`/api/proof/reject-proof`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        },
        body: JSON.stringify({
          presentationExchangeId: presentationExchangeId,
          reason: reason
        })
      });

      const data: boolean = await response.json();
      return data === true;
    } catch (error) {
      log.error('Error verifying presentation:', error);
      return false;
    }
  }

  /**
   * Get All Items
   *
   * @return {*}  {(Promise<AllItems | undefined>)}
   * @memberof TractionService
   */
  public async getAllItems(): Promise<AllItems | undefined> {
    try {
      const response = await fetch('/api/general/get-all-items', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch items');
      }

      const data: AllItems = await response.json();
      return data;
    } catch (error) {
      log.error('Error getting items: ', error);
    }
  }

  /**
   * Remove Credential
   *
   * @param {string} cred_id
   * @return {*}  {Promise<boolean>}
   * @memberof TractionService
   */
  public async removeCredential(cred_id: string): Promise<boolean> {
    try {
      const response = await fetch(`/api/credential/remove/${cred_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove credential');
      }

      const data: boolean = await response.json();
      return data === true;
    } catch (error) {
      log.error('Error removing credential: ', error);
      return false;
    }
  }

  /**
   * Request Wallet
   *
   * @param {string} email
   * @param {string} name
   * @return {*}  {Promise<IUserReservation | null>}
   * @memberof TractionService
   */
  public async requestWallet(
    email: string,
    name: string
  ): Promise<{
    reservation: IUserReservation;
    onboarding_url: string;
  } | null> {
    try {
      const response = await fetch(`/api/public/request-wallet`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${this.token}`
        },
        body: JSON.stringify({
          email: email,
          name: name
        })
      });

      if (!response.ok) {
        throw new Error('Failed to request wallet');
      }

      const data: {
        reservation: IUserReservation;
        onboarding_url: string;
      } = await response.json();

      if (!data.reservation) {
        throw new Error('Failed to request wallet');
      }

      return data;
    } catch (error) {
      log.error('Error requesting wallet: ', error);
      return null;
    }
  }
}
